import React, { useCallback, useContext, useEffect, useState } from 'react';
import xml2js from 'xml2js';

import { Banner, Round, RoundIntro } from '../Play';
import { api } from '../../utils/api';
import { MODES, MODE_SPECS } from '../../utils/constants';
import ScreenTextContext from '../../contexts/ScreenTextContext';

const hasIntersection = (arr1, arr2) => {
  return arr1.filter(item => arr2.includes(item)).length > 0
}

// init score cards with array for each round, filled with null for each task
const initScores = (game) => {

  const roundsSpecs = MODE_SPECS[game.mode];

  return game.teams.map(() => {
    const roundScores = [];

    roundsSpecs.forEach(mode => {
      const taskMarkers = [];

      // don't need the task markers for lecture mode
      const taskCount = mode.taskCount ? mode.taskCount :  Math.floor(game.lecture.length / 2);

      for(let i = 0; i < taskCount; i++) {
        taskMarkers.push(null);
      }
      roundScores.push(taskMarkers);
    })

    return roundScores;
  })
  
}

export const Play = ({game}) => {

  const roundsSpecs = MODE_SPECS[game.mode];
  
  const [allTaskGroups, setAllTaskGroups] = useState([]);
  const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
  const [roundStarted, setRoundStarted] = useState(false);
  const [scores, setScores] = useState(initScores(game));
  const [taskGroupIndex, setTaskGroupIndex] = useState([]);
  const [turn, setTurn] = useState(game.initialTurn);
  const [validTaskGroups, setValidTaskGroups] = useState([]);
  const [viewingFeedback, setViewingFeedback] = useState(false);

  const { screenText } = useContext(ScreenTextContext);

  // Fetch the tasks on load
  useEffect(() => {

    // get & set the tasks
    api.getTasksForLanguage(screenText.language)
      .then((res) => {
        
        let parser = new xml2js.Parser();
        parser.parseStringPromise(res.data)
          .then(parsedData => {
            setAllTaskGroups(parsedData.HAZWOPERCHALLENGE.TASKGROUP);
          })
          .catch(err => {
            console.log(err)
          })
      })
      .catch(err => {
        console.log(err)
      })

  }, [screenText.language])


  // For Lecture mode, loop through the lecture task ids "imported" at the Category
  // stage, and get the actual tasks for those ids.
  const getMatchingLectureTasks = useCallback((game, groups) => {
    let matchingTasks = [];

    // Flatten tasks so we can get them easily by ID in the lecture array
    const allTasks = groups.reduce((tasks, group) => (
      [...tasks, ...group.TASK]
    ), [])

    game.lecture.forEach(task => {

      // Get the task with the provided id
      const foundTask = allTasks.find(t => task === t.$.id);

      if (foundTask) {

        // Only keep the ones that are the correct role.
        const taskMatchesRole = foundTask.$.roles.indexOf(game.role) > -1;

        if (taskMatchesRole) {
          matchingTasks.push(foundTask)
        }

      }

    });

    // If odd, remove the last question
    if (matchingTasks.length % 2) {
      matchingTasks.pop();
    }

    return matchingTasks;

  }, [])

  const getMatchingTasks = useCallback((game, group) => {
    return group.TASK.filter(task => (
      // categories match
      hasIntersection(game.categories, task.$.categories) 
      && 
      // role matches
      task.$.roles.indexOf(game.role) > -1  
    ));
  }, [])

  const getTasksForRound = useCallback((currentRoundIndex) => {

    const roundsSpecs = MODE_SPECS[game.mode];

    const isLectureMode = game.mode === MODES.LECTURE;
    const { taskGroupType, taskCount } = roundsSpecs[currentRoundIndex];

    // Get the matching task group types
    const groups = allTaskGroups.filter((group) => taskGroupType.indexOf(group.$.type) > -1)

    if (isLectureMode) {
      const matchingTasks = getMatchingLectureTasks(game, groups);

      // This is not an ideal way to do this; it done this way because last-minute, 
      // SWA wanted to include SCENARIO questions in Lecture mode and it wasn't 
      // built to work that way
      groups[0].TASK = matchingTasks;
      setValidTaskGroups( [groups[0]] );
      setTaskGroupIndex(0);
      
    } else {

      const matchingGroups = groups.reduce((matchingGroups, group) => {

        // Filter out non-matching categories and roles
        let matchingTasks = [];
  
        matchingTasks = getMatchingTasks(game, group);
        
        // If there are enough questions left in the task group (or no limit on questions), keep it
        if ( !taskCount || matchingTasks.length >= (taskCount * game.teams.length) ) {
          group.TASK = matchingTasks;
          matchingGroups.push(group);
        }      
  
        return matchingGroups;
      }, [])
      
      if (matchingGroups.length === 0) {
        throw new Error("There are no tasks groups with enough questions to meet the game mode and category selections.")
      }

      const randomNumber = Math.floor(Math.random() * matchingGroups.length);
      setValidTaskGroups(matchingGroups);
      setTaskGroupIndex(randomNumber);
  
    }

  }, [game, getMatchingTasks, getMatchingLectureTasks, allTaskGroups])

  // When round changes, load new tasks
  useEffect(() => {

    if (allTaskGroups.length < 1) {
      return;
    }

    getTasksForRound(currentRoundIndex);
  }, [currentRoundIndex, getTasksForRound, allTaskGroups])


  if (!roundsSpecs) {
    console.log('No round definitions associated with game mode "' + game.mode + '"')
    return "A problem occurred loading this game mode."
  }

  const isLastRound = !roundsSpecs[currentRoundIndex + 1];

  const goToNextRound = () => {
    const nextRoundIndex = currentRoundIndex + 1;
    if (isLastRound) {
      // pass score info up
      game.completeGame(scores)
    } else {
      // set turn to the opposite of whoever went first
      // in the first round
      if (game.initialTurn) {
        setTurn(0)
      } else {
        setTurn(1)
      }
      
      setRoundStarted(false);
      setCurrentRoundIndex(nextRoundIndex);
    }
  }

  const changeTurn = () => {
    if (turn) {
      setTurn(0);
    } else {
      setTurn(1);
    }
  }

  const getNextEmptyScoreIndex = (round) => {
    
    const taskCount = round.length ? round.length : Math.floor(game.lecture.length / 2);

    for (let i = 0; i < taskCount; i++) {
      if (round[i] === null) return i;
    }
  }

  const completeTask = (success) => {
    // quick and dirty deep clone
    const newScores = [...scores];
    const currentTeamScore = [...newScores[turn]];
    const currentRoundScore = [...currentTeamScore[currentRoundIndex]];

    const emptySlot = getNextEmptyScoreIndex(currentRoundScore);
    
    currentRoundScore[emptySlot] = success;
    currentTeamScore[currentRoundIndex] = currentRoundScore;
    newScores[turn] = currentTeamScore;

    setScores(newScores);

  }

  const swapTaskGroup = () => {
    // > 1 because right now each round only has one task group
    // if this were to change, it should be defined in MODE_SPECS
    if (validTaskGroups.length > 1) {
      const newTaskGroups = [...validTaskGroups];

      // remove current task group from list
      newTaskGroups.splice(taskGroupIndex, 1);

      const randomNumber = Math.floor(Math.random() * newTaskGroups.length);
      setValidTaskGroups(newTaskGroups);
      setTaskGroupIndex(randomNumber);
    }
  } 

  if (!validTaskGroups[taskGroupIndex] || validTaskGroups[taskGroupIndex].length === 0) {
    return "Loading"
  }


  return (
    <div className="play">

      {!roundStarted ?
        <RoundIntro 
          game={game} 
          currentRoundIndex={currentRoundIndex} 
          roundsCount={roundsSpecs.length} 
          setRoundStarted={setRoundStarted}
          totalTaskCount={validTaskGroups[taskGroupIndex].TASK.length}
        />
      : 
        <>
          <Banner 
            currentRoundIndex={currentRoundIndex}
            mode={game.mode}
            scores={scores} 
            teams={game.teams} 
            turn={turn} 
            viewingFeedback={viewingFeedback}
          />
          <div className="play__container">
            <Round 
              game={game} 
              changeTurn={changeTurn}
              completeTask={completeTask}
              currentRoundIndex={currentRoundIndex}
              disableTaskGroupSwap={validTaskGroups.length <= 1}
              goToNextRound={goToNextRound}
              isLastRound={isLastRound}
              setViewingFeedback={setViewingFeedback}
              swapTaskGroup={swapTaskGroup}
              taskGroup={validTaskGroups[taskGroupIndex]}
              viewingFeedback={viewingFeedback}
            />
          </div>
        </>
      }
      
    </div>
  )
}